/**
 * Bridge between the page and the service worker's durable timer.
 *
 * While a tab is open, the dedicated web worker is the live timer. Every state
 * change is mirrored here into the service worker, which persists it
 * (timestamp-based) in IndexedDB. If the tab is closed or the page is frozen,
 * the SW can reconstruct exactly where the timer should be whenever it wakes,
 * and the page restores from it on reopen.
 */

import type { WorkerState, TimerStage } from '../workers/timer.worker';
import { debugLog } from './debugLog';

/** Shape of the state the service worker sends back (ClientSyncState in the SW). */
export interface SWTimerSnapshot {
  stage: TimerStage;
  isRunning: boolean;
  currentPomodoro: number;
  completedPomodoros: number;
  totalCompletedCycles: number;
  timestamp: number | null;
  calculatedRemaining: number;
  settings: Record<string, unknown>;
}

function getController(): ServiceWorker | null {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.controller;
}

/** Mirror the web worker's full timer state into the service worker. */
export function mirrorTimerStateToSW(workerState: WorkerState): void {
  const controller = getController();
  if (!controller) return;
  controller.postMessage({ type: 'SET_STATE', state: workerState });
}

/**
 * Ask the service worker for its (reconciled) timer state.
 *
 * Resolves to:
 * - a snapshot when the SW has timer state,
 * - `null` when the SW explicitly reports no timer (stopped/reset),
 * - `undefined` when there is no controlling SW or it did not answer in time.
 */
export function querySWTimerState(timeoutMs = 1500): Promise<SWTimerSnapshot | null | undefined> {
  const controller = getController();
  if (!controller) return Promise.resolve(undefined);

  return new Promise((resolve) => {
    let settled = false;

    const finish = (value: SWTimerSnapshot | null | undefined) => {
      if (settled) return;
      settled = true;
      navigator.serviceWorker.removeEventListener('message', onMessage);
      resolve(value);
    };

    const onMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'TIMER_UPDATE') {
        finish((event.data.state as SWTimerSnapshot | null) ?? null);
      }
    };

    navigator.serviceWorker.addEventListener('message', onMessage);
    controller.postMessage({ type: 'SYNC_TIMER' });
    setTimeout(() => finish(undefined), timeoutMs);
  });
}

/**
 * Register background wake-ups so the SW can fire completion notifications with
 * all tabs closed. Periodic Background Sync is Chromium-only and requires an
 * installed PWA; everything degrades gracefully elsewhere.
 */
export async function registerBackgroundReconcile(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const registration = await navigator.serviceWorker.ready;

    const periodicSync = (registration as unknown as { periodicSync?: { register(tag: string, options: { minInterval: number }): Promise<void> } }).periodicSync;
    if (periodicSync) {
      try {
        const permissions = (navigator as unknown as { permissions?: { query(desc: { name: string }): Promise<{ state: string }> } }).permissions;
        const status = permissions ? await permissions.query({ name: 'periodic-background-sync' }) : null;
        if (!status || status.state === 'granted') {
          await periodicSync.register('pomodoro-reconcile', { minInterval: 60 * 1000 });
          debugLog('[SW Bridge] Periodic background sync registered.');
        }
      } catch {
        // Permission denied or API rejected — background notifications will
        // only fire when the SW wakes for other reasons.
      }
    }

    const sync = (registration as unknown as { sync?: { register(tag: string): Promise<void> } }).sync;
    if (sync) {
      try {
        await sync.register('pomodoro-reconcile');
      } catch {
        // Background sync unavailable; non-fatal.
      }
    }
  } catch {
    // Service worker never became ready; nothing to register.
  }
}
